import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useApp } from "../context";
import stravaButton from "../assets/stravaButton4X.png";
export function Mark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 40 40">
      <rect x="1" y="1" width="38" height="38" rx="4" fill="currentColor" />
      <path d="M8 27 18 10l7 12 4-7 5 12H22l-4-7-4 7Z" fill="#f8efdb" />
    </svg>
  );
}
export function Header() {
  const { profile, demo, signOut } = useApp();
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  return (
    <>
      <header className="site-header">
        <Link className="brand" to="/">
          <Mark />
          <span>
            Strava Wrapped<small>YOUR YEAR, IN MOTION</small>
          </span>
        </Link>
        <nav aria-label="Main navigation">
          {profile && (
            <>
              <NavLink activeClassName="current" to="/yourwrapped">
                Your year
              </NavLink>
              <NavLink activeClassName="current" to="/yourart">
                Route art
              </NavLink>
            </>
          )}
          <NavLink activeClassName="current" to="/about">
            About
          </NavLink>
          {profile && (
            <button
              className="text-button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await signOut();
                } catch (e) {
                  setError(e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Signing out…" : demo ? "Exit demo" : "Sign out"}
            </button>
          )}
        </nav>
      </header>
      {error && (
        <div className="notice" role="alert">
          {error}
        </div>
      )}
    </>
  );
}
export function Footer() {
  return (
    <footer className="site-footer">
      <span>
        Made for the miles. Built by{" "}
        <a href="https://ankitagrawal.com">Ankit</a>.
      </span>
      <span>Powered by Strava · An independent project</span>
      <Link to="/about">Privacy & help ↗</Link>
    </footer>
  );
}
export function Window({ title, children, className = "" }) {
  return (
    <section className={`window ${className}`}>
      <div className="window-bar">
        <span>
          <i /> {title}
        </span>
        <span aria-hidden="true">− &nbsp; □ &nbsp; ×</span>
      </div>
      {children}
    </section>
  );
}
export function Connect({ className = "", force = false }) {
  return (
    <a
      className={`strava-connect ${className}`}
      href={`/api/strava?action=login${force ? "&prompt=force" : ""}`}
    >
      <img src={stravaButton} alt="Connect with Strava" />
    </a>
  );
}
export function Filters() {
  const { year, setYear, units, setUnits, profile } = useApp();
  const current = new Date().getFullYear();
  return (
    <div className="filters">
      <label>
        Year{" "}
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {Array.from({ length: current - 2008 }, (_, i) => current - i).map(
            (y) => (
              <option key={y}>{y}</option>
            ),
          )}
        </select>
      </label>
      <div className="segmented" aria-label="Distance units">
        {["km", "mi"].map((u) => (
          <button
            key={u}
            aria-pressed={units === u}
            onClick={() => setUnits(u)}
          >
            {u}
          </button>
        ))}
      </div>
      <span className="connection-status">
        <i />
        {profile?.firstname} · connected
      </span>
    </div>
  );
}
export function DemoBanner() {
  const { demo } = useApp();
  return demo ? (
    <div className="demo-banner">
      <span>EXAMPLE DATA</span> You’re exploring a sample year. Connect Strava
      to see your own.
      <Connect />
    </div>
  ) : null;
}
export function ErrorState({ error, retry }) {
  const [remaining, setRemaining] = React.useState(error.retryAfter || 0);
  React.useEffect(() => {
    setRemaining(error.retryAfter || 0);
    const timer = setInterval(
      () => setRemaining((v) => Math.max(0, v - 1)),
      1000,
    );
    return () => clearInterval(timer);
  }, [error]);
  const reconnect = ["session_expired", "permission_denied"].includes(
    error.code,
  );
  return (
    <div className="empty-state" role="alert">
      <p className="eyebrow">A QUICK PIT STOP</p>
      <h2>
        {["subscription_required", "api_access_denied"].includes(error.code)
          ? "Strava is blocking this app’s access."
          : "We couldn’t load your activities."}
      </h2>
      <p>{error.message}</p>
      {remaining > 0 && (
        <p>
          Try again in {Math.ceil(remaining / 60)}{" "}
          {Math.ceil(remaining / 60) === 1 ? "minute" : "minutes"}.
        </p>
      )}
      {reconnect ? (
        <Connect force={error.code === "permission_denied"} />
      ) : (
        <button className="button" onClick={retry} disabled={remaining > 0}>
          Try again ↻
        </button>
      )}
    </div>
  );
}
export function Loading({ progress = 0, text = "Loading your activities" }) {
  return (
    <div className="empty-state" role="status">
      <div className="loader" />
      <h2>{text}</h2>
      <p>
        {progress
          ? `${progress} activities received. Finishing your year…`
          : "This can take a moment. We only request the year you selected."}
      </p>
    </div>
  );
}
